import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import VibeEmoji from "./VibeEmoji";

/**
 * Big "limited-time featured" banner for the Date Night experience pack.
 * Sits above the regular "Tonight's vibe" hero packs row.
 *
 * Props:
 *   pack — the date-night pack object from /api/packs
 *   onClick — selects the pack and scrolls back to Create flow
 */
export default function DateNightFeature({ pack, onClick }) {
  if (!pack) return null;
  const vibe = pack.vibe || {};
  const gradient =
    vibe.gradient ||
    "linear-gradient(135deg, #FFD45E 0%, #FF6B9D 50%, #C8378F 100%)";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.985 }}
      data-testid="date-night-feature"
      className="relative w-full text-left rounded-3xl overflow-hidden border-2 group block"
      style={{
        background: gradient,
        borderColor: "rgba(255, 212, 94, 0.85)",
        boxShadow:
          "0 24px 70px rgba(0,0,0,0.45), 0 0 90px rgba(255, 107, 157, 0.55), 0 0 0 1px rgba(255, 212, 94, 0.4)",
      }}
    >
      {/* Subtle film-grain overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.18) 0%, transparent 35%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.18) 0%, transparent 40%)",
        }}
      />
      {/* Light sweep (continuous) */}
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "linear" }}
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.20) 50%, transparent 100%)",
          backgroundSize: "45% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />

      <div className="relative p-5 md:p-7">
        {/* Top row */}
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.25em] bg-white/15 backdrop-blur-sm border border-white/20 text-white px-2.5 py-1 rounded-full">
            <Sparkles className="w-3 h-3" /> Limited-time featured
          </span>
          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.2em] bg-black/30 border border-white/15 text-white px-2 py-0.5 rounded-full">
            Free
          </span>
        </div>

        {/* Hero row */}
        <div className="flex items-start gap-4 md:gap-5">
          <div className="flex-shrink-0">
            <VibeEmoji
              emoji={pack.emoji || "🍷"}
              animationKey={vibe.animation || "flicker"}
              size="text-6xl md:text-7xl"
              glow="#FFB547"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80 mb-1">
              We plan it, you show up.
            </p>
            <h3
              className="font-display italic text-white text-3xl md:text-4xl leading-[1.05] mb-2"
              style={{ textShadow: "0 2px 16px rgba(0,0,0,0.35)" }}
            >
              {pack.name}
            </h3>
            <p className="text-sm md:text-base text-white/90 leading-snug">
              Mini missions · flirty prompts · photo tasks · playful decisions.
            </p>
          </div>
        </div>

        {/* Mission preview chips */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(pack.preview || [
            "Order each other's drink",
            "Take a selfie recreating your first photo",
            "Describe your perfect future together",
          ])
            .slice(0, 3)
            .map((m, i) => (
              <span
                key={i}
                className="text-[11px] font-semibold text-white bg-black/25 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1 line-clamp-1"
              >
                {m}
              </span>
            ))}
        </div>

        {/* CTA */}
        <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-white">
          Tap to plan tonight <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </motion.button>
  );
}
